/**
 * MirrorFreshness — tiny header pill showing how fresh the dashboard data is.
 *
 * Backed by the `sync_info` block that /api/dashboard returns (and the standalone
 * /api/mirror/status endpoint). Ticks every second locally so the displayed age
 * stays current between dashboard reloads. Three colour states:
 *
 *   • green  — fresh (< 90s)            → "Synced 14s ago"
 *   • amber  — getting old (90s - 5min) → "Synced 2m ago"
 *   • rose   — stale / live fallback    → "Live · slow mode"  /  "Sync stalled"
 *
 * Keeps the same vertical footprint as the old "Live · MS SQL" subtitle so the
 * header layout doesn't shift.
 */
import { useEffect, useState } from "react";
import { Database, Zap, AlertCircle } from "lucide-react";

function fmtAge(secs) {
  if (secs == null) return "—";
  if (secs < 60) return `${secs}s ago`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

export default function MirrorFreshness({ syncInfo, compact = false }) {
  // Re-render every second so the displayed age stays in sync with wall clock.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (!syncInfo) {
    return (
      <div className={`text-cyan-100 ${compact ? "text-[9px]" : "text-[10px] sm:text-[11px]"} tracking-wider uppercase`} data-testid="mirror-freshness-loading">
        Loading…
      </div>
    );
  }

  // Recompute age locally from last_sync_at + wall clock so the value ticks
  // between API responses. Falls back to server-reported age_seconds if no ISO.
  let age = syncInfo.age_seconds;
  if (syncInfo.last_sync_at) {
    const ms = Date.now() - new Date(syncInfo.last_sync_at).getTime();
    age = Math.max(0, Math.floor(ms / 1000));
  }

  const live = syncInfo.source === "live";
  const stale = !!syncInfo.stale;
  const available = !!syncInfo.available;

  // Choose colour + icon + label based on health.
  let cls = "text-emerald-100";
  let Icon = Zap;
  let label;
  let testid = "mirror-freshness-fresh";
  if (!available || !syncInfo.last_sync_at) {
    cls = "text-rose-100";
    Icon = AlertCircle;
    label = live ? "Live · slow mode" : "Sync starting…";
    testid = "mirror-freshness-pending";
  } else if (stale) {
    cls = "text-rose-100";
    Icon = AlertCircle;
    label = live ? "Live · slow mode" : `Sync stalled · ${fmtAge(age)}`;
    testid = "mirror-freshness-stale";
  } else if (age != null && age > 90) {
    cls = "text-amber-100";
    Icon = Database;
    label = `Synced ${fmtAge(age)}`;
    testid = "mirror-freshness-aging";
  } else {
    label = `Synced ${fmtAge(age)}`;
  }

  const sizeCls = compact ? "text-[9px]" : "text-[10px] sm:text-[11px]";
  return (
    <div
      className={`flex items-center gap-1 ${cls} ${sizeCls} tracking-wider uppercase whitespace-nowrap`}
      data-testid={testid}
      title={
        syncInfo.last_sync_at
          ? `Last full sync: ${new Date(syncInfo.last_sync_at).toLocaleString()}\nMirror docs: ${syncInfo.last_count_total ?? "?"}`
          : "Mirror not yet populated"
      }
    >
      <Icon size={compact ? 9 : 10} className="opacity-80" />
      <span>{label}</span>
    </div>
  );
}
